import { useNavigate } from "react-router-dom";
import type { IndividuoArboreo } from "@/types/ui-models";
import TreeRiskBadge from "./TreeRiskBadge";
import TreeReviewBadge from "./TreeReviewBadge";
import { tipoIndividuoLabel } from "@/config/cta-mapa-config";

interface TreeTableProps {
  individuos: IndividuoArboreo[];
}

function formatDate(value: string): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

const th = "text-left px-3 py-3 text-xs font-medium text-foreground-500 whitespace-nowrap";

/** Tabela do cadastro com as colunas que a listagem real fornece. */
export default function TreeTable({ individuos }: TreeTableProps) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-card border border-background-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-background-200 bg-background-50">
              <th className={th}>Código CTA</th>
              <th className={th}>Tipo</th>
              <th className={th}>Espécie</th>
              <th className={th}>Projeto</th>
              <th className={th}>Localização</th>
              <th className={th}>Risco</th>
              <th className={th}>Última vistoria</th>
              <th className={th}>Revisão</th>
              <th className={th}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {individuos.map((ind) => (
              <tr key={ind.id} className="border-b border-background-200 hover:bg-background-50 transition-colors">
                <td className="px-3 py-3">
                  <p className="text-xs font-mono text-foreground-600 whitespace-nowrap">{ind.codigoCta || "—"}</p>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 bg-background-100 px-2 py-0.5 rounded-md whitespace-nowrap">
                    {tipoIndividuoLabel(ind.tipo)}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <p className="text-sm font-medium text-foreground-900 whitespace-nowrap">{ind.nomePopular || "Não identificada"}</p>
                  {ind.nomeCientifico && (
                    <p className="text-xs text-foreground-400 italic">{ind.nomeCientifico.split(" ").slice(0, 2).join(" ")}</p>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600 max-w-[120px] truncate block">{ind.projeto || "—"}</span>
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-600">
                    {[ind.bairro, ind.logradouro].filter(Boolean).join(" / ") || "—"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <TreeRiskBadge risco={ind.classeRisco} />
                </td>
                <td className="px-3 py-3">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(ind.ultimaVistoria)}</span>
                </td>
                <td className="px-3 py-3">
                  <TreeReviewBadge status={ind.statusRevisao} />
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => navigate(`/cta/${ind.id}`)}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 whitespace-nowrap cursor-pointer"
                    title="Ver FCA"
                  >
                    <i className="ri-file-chart-line"></i> Ver FCA
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
